import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Search, ShoppingBag, Globe, ArrowUpRight } from "lucide-react";
import { useCart } from "./CartContext";
import { useStoreRegion } from "./StoreRegionContext";
import { apiClient } from "../lib/api";

const MENU_LINKS = [
  { key: "home", label: "Home", to: "/", num: "01" },
  { key: "all", label: "All Candles", to: "/?collection=all#shop", num: "02" },
  { key: "pillar", label: "Pillar Candles", to: "/?collection=pillar#shop", num: "03" },
  { key: "designer", label: "Designer Candles", to: "/?collection=designer#shop", num: "04" },
  { key: "jar", label: "Jar Candles", to: "/?collection=jar#shop", num: "05" },
  { key: "contact", label: "Contact", to: "/contact", num: "06" },
];

function useActiveKey() {
  const { pathname, search } = useLocation();
  const collection = new URLSearchParams(search).get("collection");
  if (pathname === "/contact") return "contact";
  if (pathname === "/") {
    if (!collection || collection === "all") {
      // Home is active only when there's no collection filter applied.
      return collection === "all" ? "all" : "home";
    }
    return collection; // pillar / designer / jar
  }
  return null;
}

const Logo = ({ className = "" }) => (
  <span
    className={`font-black tracking-tighter whitespace-nowrap ${className}`}
    style={{ fontFamily: "Outfit" }}
  >
    SMIBHA <span className="italic font-light luxe-text mx-1.5">Luxe</span> CANDLES
  </span>
);

export default function StoreHeader() {
  const { count, setOpen } = useCart();
  const { region, setRegion, regions } = useStoreRegion();
  const navigate = useNavigate();
  const activeKey = useActiveKey();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allProducts, setAllProducts] = useState(null);
  const searchInputRef = useRef(null);

  // Lazy-load catalog for search suggestions (once).
  useEffect(() => {
    if (searchOpen && allProducts === null) {
      apiClient
        .get("/store/products")
        .then((r) => setAllProducts(r.data || []))
        .catch(() => setAllProducts([]));
    }
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  }, [searchOpen, allProducts]);

  // Lock background scroll while an overlay is open.
  useEffect(() => {
    const lock = menuOpen || searchOpen;
    document.body.style.overflow = lock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, searchOpen]);

  // Escape closes overlays.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !allProducts) return [];
    return allProducts
      .filter((p) => {
        const hay = `${p.name || ""} ${p.description || ""} ${p.collection || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [query, allProducts]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const goToProduct = (id) => {
    closeSearch();
    navigate(`/products/${id}`);
  };

  return (
    <>
      {/* Announcement bar */}
      <div
        className="bg-zinc-900 text-white text-center text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase py-2 px-3 font-semibold"
        data-testid="announcement-bar"
      >
        Hand-poured in Cambridge, Ontario · Small-batch luxury candles
      </div>

      <header
        className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-zinc-100 z-50"
        data-testid="store-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          {/* Left — hamburger */}
          <div className="flex items-center justify-start">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="hamburger-btn"
              aria-label="Open menu"
              className="p-2 -ml-2 rounded-full hover:bg-zinc-100 active:scale-95 transition"
            >
              <Menu size={24} strokeWidth={1.75} />
            </button>
          </div>

          {/* Center — logo */}
          <div className="flex items-center justify-center min-w-0">
            <Link to="/" className="flex items-center min-w-0" data-testid="store-logo">
              <Logo className="text-sm sm:text-lg md:text-2xl" />
            </Link>
          </div>

          {/* Right — search + cart + region */}
          <div className="flex items-center justify-end gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="search-btn"
              aria-label="Search products"
              className="p-2 rounded-full hover:bg-zinc-100 active:scale-95 transition"
            >
              <Search size={22} strokeWidth={1.6} />
            </button>

            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="open-cart-btn"
              aria-label="Open cart"
              className="relative p-2 rounded-full hover:bg-zinc-100 active:scale-95 transition"
            >
              <ShoppingBag size={22} strokeWidth={1.6} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 luxe-bg text-white min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold leading-none"
                >
                  {count}
                </span>
              )}
            </button>

            {/* Region picker — hidden on small screens (also available in the menu) */}
            <div className="hidden lg:flex items-center gap-1.5 ml-2 pl-3 border-l border-zinc-200">
              <Globe size={14} strokeWidth={1.5} className="text-zinc-500" />
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                data-testid="region-picker"
                aria-label="Select region"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold bg-transparent focus:outline-none cursor-pointer"
              >
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* ---------- Search overlay ---------- */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="fixed inset-0 z-[60]"
            data-testid="search-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm"
              onClick={closeSearch}
              data-testid="search-backdrop"
            />
            <motion.div
              className="absolute top-0 left-0 right-0 bg-[#FBF8F3] shadow-xl"
              initial={{ y: -24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -24, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="flex items-center gap-3 border-b-2 border-zinc-900 pb-3">
                  <Search size={22} strokeWidth={1.6} className="luxe-text shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search candles, scents, collections…"
                    data-testid="search-input"
                    className="flex-1 bg-transparent text-lg sm:text-2xl font-light tracking-tight focus:outline-none placeholder:text-zinc-400"
                    style={{ fontFamily: "Outfit" }}
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery("");
                        searchInputRef.current?.focus();
                      }}
                      data-testid="search-clear-btn"
                      aria-label="Clear search"
                      className="p-1.5 rounded-full hover:bg-zinc-200 transition text-zinc-500"
                    >
                      <X size={18} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeSearch}
                    data-testid="search-close-btn"
                    aria-label="Close search"
                    className="ml-1 p-1.5 rounded-full hover:bg-zinc-200 transition text-zinc-500"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="mt-5 max-h-[60vh] overflow-y-auto">
                  {!query && (
                    <p
                      className="text-xs uppercase tracking-[0.25em] text-zinc-400 py-4"
                      data-testid="search-hint"
                    >
                      Start typing to search the collection
                    </p>
                  )}

                  {query && results.length === 0 && allProducts !== null && (
                    <p className="py-6 text-sm text-zinc-500" data-testid="search-no-results">
                      No candles match “{query}”. Try a different scent or name.
                    </p>
                  )}

                  {results.length > 0 && (
                    <div data-testid="search-results">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-3">
                        {results.length} {results.length === 1 ? "result" : "results"}
                      </div>
                      <ul className="divide-y divide-zinc-200">
                        {results.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => goToProduct(p.id)}
                              data-testid={`search-result-${p.id}`}
                              className="w-full flex items-center gap-4 py-3 text-left group"
                            >
                              <div className="w-14 h-14 bg-zinc-100 overflow-hidden shrink-0">
                                <img
                                  src={p.image_url}
                                  alt={p.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div
                                  className="text-sm sm:text-base font-bold leading-tight group-hover:luxe-text transition truncate"
                                  style={{ fontFamily: "Outfit" }}
                                >
                                  {p.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-0.5">
                                  {p.collection || "designer"} candle
                                </div>
                              </div>
                              <ArrowUpRight
                                size={18}
                                className="text-zinc-300 group-hover:luxe-text transition shrink-0"
                              />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Menu overlay ---------- */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[70]"
            data-testid="menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              className="absolute inset-0"
              onClick={() => setMenuOpen(false)}
              data-testid="menu-backdrop"
            />
            <motion.nav
              className="absolute inset-y-0 left-0 w-full sm:max-w-md flex flex-col text-[#FBF8F3] overflow-y-auto"
              style={{
                background:
                  "linear-gradient(160deg, #241a12 0%, #3a2717 55%, #1c130c 100%)",
              }}
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 sm:px-8 h-16 sm:h-20 border-b border-white/10">
                <span
                  className="text-sm sm:text-base font-black tracking-tighter"
                  style={{ fontFamily: "Outfit" }}
                >
                  SMIBHA <span className="italic font-light mx-1.5" style={{ color: "#C68B59" }}>Luxe</span> CANDLES
                </span>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  data-testid="menu-close-btn"
                  aria-label="Close menu"
                  className="p-2 -mr-2 rounded-full hover:bg-white/10 active:scale-95 transition"
                >
                  <X size={26} strokeWidth={1.75} />
                </button>
              </div>

              <div className="flex-1 px-6 sm:px-8 py-8 sm:py-10">
                <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-6">
                  Menu
                </div>
                <ul className="space-y-1">
                  {MENU_LINKS.map((link, i) => {
                    const active = activeKey === link.key;
                    return (
                      <motion.li
                        key={link.key}
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08 + i * 0.05, duration: 0.35 }}
                      >
                        <NavLink
                          to={link.to}
                          onClick={() => setMenuOpen(false)}
                          data-testid={`menu-link-${link.key}`}
                          className="group flex items-baseline gap-4 py-2.5 border-b border-white/5"
                        >
                          <span
                            className="text-[10px] font-mono tabular-nums"
                            style={{ color: active ? "#C68B59" : "rgba(255,255,255,0.35)" }}
                          >
                            {link.num}
                          </span>
                          <span
                            className="text-3xl sm:text-4xl font-black tracking-tight transition-colors"
                            style={{
                              fontFamily: "Outfit",
                              color: active ? "#C68B59" : "#FBF8F3",
                            }}
                          >
                            {link.label}
                          </span>
                          {active && (
                            <span
                              className="ml-1 w-1.5 h-1.5 rounded-full self-center"
                              style={{ backgroundColor: "#C68B59" }}
                              data-testid={`menu-active-dot-${link.key}`}
                            />
                          )}
                        </NavLink>
                      </motion.li>
                    );
                  })}
                </ul>
              </div>

              {/* Region picker inside menu (mobile-friendly) */}
              <div className="px-6 sm:px-8 py-6 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                  Shipping region
                </div>
                <div className="flex items-center gap-2">
                  <Globe size={16} strokeWidth={1.5} className="text-white/60" />
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    data-testid="menu-region-picker"
                    aria-label="Select region"
                    className="bg-transparent text-sm uppercase tracking-[0.2em] font-semibold focus:outline-none cursor-pointer text-[#FBF8F3]"
                  >
                    {regions.map((r) => (
                      <option key={r.code} value={r.code} className="text-zinc-900">
                        {r.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
